import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import CloseIcon from '@/Domain/components/icons/Close.icon';

interface EmailDialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  testId: string;
  closeTestId: string;
  doneTestId: string;
  children: ReactNode;
}

/** Card dialog centred inside the Email sheet (absolute, not fixed) with a title, close X and Done footer. */
export const EmailDialogShell: React.FC<EmailDialogShellProps> = ({
  open,
  onOpenChange,
  title,
  subtitle,
  testId,
  closeTestId,
  doneTestId,
  children,
}) => {
  const { t } = useTranslation(['integrations']);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
      <DialogPrimitive.Content
        className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
        data-testid={testId}
        aria-describedby={undefined}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          onOpenChange(false);
        }}
        // Clicking the backdrop around the card (the content padding, not a
        // child) dismisses it, so X is not the only way out.
        onClick={(e) => {
          if (e.target === e.currentTarget) onOpenChange(false);
        }}
      >
        <div className="flex max-h-full w-full max-w-lg flex-col overflow-y-auto rounded-lg bg-card p-6 shadow-lg">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1.5">
              <DialogPrimitive.Title className="text-lg font-semibold text-foreground">
                {title}
              </DialogPrimitive.Title>
              {subtitle ? (
                <p className="text-sm text-muted-foreground text-pretty">{subtitle}</p>
              ) : null}
            </div>
            <IconButton
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className="shrink-0"
              data-testid={closeTestId}
            >
              <CloseIcon />
            </IconButton>
          </div>

          {children}

          <div className="mt-6 flex justify-end">
            <Button
              type="button"
              variant="dark"
              size="md"
              onClick={() => onOpenChange(false)}
              data-testid={doneTestId}
            >
              {t('integrations:close')}
            </Button>
          </div>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};
